import React, { useState } from 'react';
import { 
  DataGrid, 
  GridColDef, 
  GridActionsCellItem 
} from '@mui/x-data-grid';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  FormControl, 
  InputLabel, 
  MenuItem, 
  Select, 
  Stack, 
  TextField, 
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { 
  useActualizarCamaraMutation,
  useAgregarCamaraMutation, 
  useBorrarCamaraMutation, 
  useGetCamarasByFrioIdQuery,  
} from '../../../api/frioApi';
import { useNavigate } from 'react-router-dom';
import { CamaraType, ItemCamara, ItemFrigorifico, ItemPlanta } from '../interfaces/avanzado.interface';
//import { frigorificos } from '../../../api/frigorificos';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { handleMutationError } from '../../../api/handleMutationError';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';

type CardProps = {
  propsPlanta: ItemPlanta//string;
  propsFrio: ItemFrigorifico//string;
}

export const AvanzadoCamaraComponent: React.FC<CardProps> = ({propsPlanta, propsFrio}) => {

  const navigate = useNavigate();

  const { data = [], error, isLoading, refetch } = useGetCamarasByFrioIdQuery({plantaId: propsPlanta.id, frioId:propsFrio.id}, {});
  const [addItem, {isLoading: isLoadingAdd, isError: isErrorAdd, error: errorAdd}] = useAgregarCamaraMutation();
  const [updateItem, {isLoading: isLoadingUpdate, isError: isErrorUpdate, error: errorUpdate}] = useActualizarCamaraMutation();
  const [deleteItem, {isLoading: isLoadingDelete, isError: isErrorDelete, error: errorDelete}] = useBorrarCamaraMutation();
  
  const [editItem, setEditItem] = useState<ItemCamara | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [selectedIdDelete, setSelectedIdDelete] = useState<string | null>(null);

  if (error) {
    if (typeof error === 'object' && error !== null) {
      if('data' in error)
      {
        return (
          <section className="alert alert-danger">
          Falla de conexion con el servidor <div>{JSON.stringify(error.data)}</div>
          </section>
        );
      }
      return (
        <section className="alert alert-danger">
          Falla de conexion con el servidor <div>{JSON.stringify(error)}</div>
        </section>
      );
    }
  }
  
  const handleAddClick = () => {
    setEditItem(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (item: ItemCamara) => {
    setEditItem(item);
    setIsDialogOpen(true);
  };

  /*const handleDeleteClick = async (id: string) => {
    await deleteItem(id);
    refetch();
  };*/

    const handleDeleteClick = (id: string) => {
    setSelectedIdDelete(id);
    setOpenDialogDelete(true);
  };

  const confirmDelete = async () => {
    try {
      if (selectedIdDelete !== null) {
        await deleteItem(selectedIdDelete).unwrap();
        setOpenDialogDelete(false);
        setSelectedIdDelete(null);
        refetch();
      }
    } catch (err) {
      // Handle error, display message
      console.error("Error al elminar la camara", err);
    }        
  };

  const cancelDelete = () => {
    setOpenDialogDelete(false);
    setSelectedIdDelete(null);
  };


  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditItem(null);
  };

  const handleSaveClick = async (plantaId: string, frioId: string) => {
    try {
      if (editItem) {
        if (editItem.id) {
          await updateItem(
            {
              id: editItem.id, 
              name: editItem.name, 
              tipo_camara: editItem.tipo_camara, 
              orden: editItem.orden, 
              planta: plantaId,
              frigorifico: frioId
            }
          ).unwrap();
        } else {
          await addItem(
            {
              name: editItem.name, 
              orden: editItem.orden, 
              tipo_camara: editItem.tipo_camara, 
              planta: plantaId,
              frigorifico: frioId
            }
          ).unwrap();
        }
        refetch();
        handleDialogClose();
      }
    } catch (err) {
      // Handle error, display message
      console.error("Error al guardar la camara", err);
    }
    
  }; 

  const handleViewSensoresClick = (planta: ItemPlanta, frio: ItemFrigorifico, camara: ItemCamara) => {
    navigate("/avanzado/sensores",{state: {planta, frio, camara}});
  };

  const handleViewPlantasClick = () => {
    navigate("/avanzado");
  };

  const handleViewFriosClick = (planta: ItemPlanta) => {
    navigate("/avanzado/frigorificos",{state: {planta}});
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Nombre', flex: 1, minWidth: 150, editable: false },
    { field: 'tipo_camara', headerName: 'Tipo', flex: 1, minWidth: 150, editable: false },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      flex: 0.5,
      minWidth: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          color="primary"
          label="Edit"
          onClick={() => handleEditClick(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          color="error"
          label="Delete"
          onClick={() => handleDeleteClick(params.row.id as string)}
        />,
      ],
    },
    { field: 'orden', headerName: 'Orden Aparicion', flex: 0.5, minWidth: 80, editable: false },
    {
      field: 'planta',
      headerName: 'Planta',
      flex: 0.7,
      minWidth: 100,
      // Accediendo al valor anidado dentro de `item`
      valueGetter: (params, row) => row.planta?.name ?? 'N/A',
    },
    {
      field: 'frigorifico',
      headerName: 'Frigorifico',
      flex: 0.7,
      minWidth: 100,
      // Accediendo al valor anidado dentro de `item`
      valueGetter: (params, row) => row.frigorifico?.name ?? 'N/A',
    },
    { field: 'id', headerName: 'ID', flex: 0.3, minWidth: 60 },
    {
      field: 'ir',
      headerName: 'Ver',
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          startIcon={<NavigateNextIcon />} 
          onClick={() => handleViewSensoresClick(propsPlanta, propsFrio, params.row)}
        >
          SENSORES
        </Button>
      ),
    }
  ];


  return (
    <div>
    {isLoading ? (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    ) :
    <Box sx={{ height: 400, width: '100%', padding: 2 }}>
      {/* Título del DataGrid */}
      <Typography variant="h5" component="h2" gutterBottom sx={{ textAlign: 'center' }}>
        Lista de camaras del frigorifico  {`${propsFrio?.name}`}
      </Typography>
      <Stack sx={{my: 2}} spacing={2} direction="row">
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleViewPlantasClick()}
          startIcon={<KeyboardDoubleArrowLeftIcon />} 
        >
          PLANTAS
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleViewFriosClick(propsPlanta)}
          startIcon={<NavigateBeforeIcon />} 
        >
          FRIGORIFICOS
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          AGREGAR CAMARA
        </Button>
      </Stack>
      <DataGrid
        rows={data!.data}
        columns={columns}
        editMode="row"
        onRowEditStop={() => {}}
      />
        
      <Dialog open={isDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{editItem ? 'Editar Camara' : 'Agregar Camara'}</DialogTitle>
        <DialogContent>
          {(isLoadingAdd || isLoadingUpdate) && <CircularProgress />}
          {isErrorAdd && <p>Error al guardar: {errorAdd? handleMutationError(errorAdd) : 'Falla no reconocida'}</p>}
          {isErrorUpdate && <p>Error al actualizar: {errorUpdate? handleMutationError(errorUpdate) : 'Falla no reconocida'}</p>}
          <TextField
            autoFocus
            margin="dense"
            label="Nombre"
            fullWidth
            variant="standard"
            value={editItem?.name || ''}
            onChange={(e) => setEditItem({ ...editItem, name: e.target.value } as ItemCamara)}
          />
          <TextField
            autoFocus
            margin="dense"
            label="Orden Aparición"
            fullWidth
            variant="standard"
            value={editItem?.orden || ''}
            onChange={(e) => setEditItem({ ...editItem, orden: e.target.value } as ItemCamara)}
          />
          <FormControl variant="standard" fullWidth >
            <InputLabel>Tipo de Camara</InputLabel>
              <Select
                value={editItem?.tipo_camara || ''}
                onChange={(e) => setEditItem({ ...editItem, tipo_camara: e.target.value } as ItemCamara)}
                fullWidth
                label="Tipo de Camara"
                
              >
                {Object.values(CamaraType).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancelar
          </Button>
          <Button onClick={() => handleSaveClick(propsPlanta.id as string, propsFrio.id as string)} color="primary">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

        <Dialog open={openDialogDelete} onClose={cancelDelete}>
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Está seguro de que desea eliminar la cámara?
            </Typography>
          </DialogContent>
          <DialogActions>
            {isLoadingDelete && <CircularProgress />}
            {isErrorDelete && <p>Error al borrar: {errorDelete? handleMutationError(errorDelete) : 'Falla no reconocida'}</p>}
            <Button onClick={cancelDelete} color="primary">
              Cancelar
            </Button>
            <Button onClick={confirmDelete} color="error">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
    }
    </div>
  );
};